import * as React from 'react';
import classNames from 'classnames';
import * as Classes from '../common/classes';
import { BaseProps, AppLayoutDetailHeaderIntent } from '../common/props';
import {
  NavBarMenuLayout,
  NavBarMenuLayoutProps,
} from '../navbar/NavBarMenuLayout';
import { SidePanel } from '../navbar/SidePanel';
import { AppLayoutTestWrapper } from './AppLayoutTestWrapper';
import { AppLayoutWaitForQueryData } from './AppLayoutWaitForQueryData';

import type { UseQueryResult } from 'react-query';

import type { BadgeCountIntent } from '../common/props';

const defaultContentWidth = 1600;
const defaultLeftPanelWidth = 210;

export interface ILayoutProps extends BaseProps {
  horizontalCenter?: boolean;
  verticalCenter?: boolean;
  /**
   * Maximální šířka obsahu stránky, standardně 1600, dá se obecně vypnout pomocí noDefaultContentWidth nebo případně nastavit na jonou hodnotu.
   */
  contentWidth?: number;
  noDefaultContentWidth?: boolean;
  /**
   * classname předávaný na nejnižší wrapper element v komponentě
   */
  contentWrapperClassName?: string;
  queries?: UseQueryResult<any, any>[];
  children: React.ReactNode | (() => React.ReactNode) | undefined;
}

export interface IAppLayoutBlankProps extends ILayoutProps {}
export type AppLayoutBlankProps = IAppLayoutBlankProps & NavBarMenuLayoutProps;

/**
 * Layout komponenta aplikace - prázdná - využití např. pro prvotní načtení aplikace, globální error stránky (js chyby) apod.
 */
export class AppLayoutBlank extends React.PureComponent<AppLayoutBlankProps> {
  render() {
    const {
      className,
      contentWrapperClassName,
      horizontalCenter,
      verticalCenter,
      children,
      contentWidth = defaultContentWidth,
      noDefaultContentWidth,
      queries,
    } = this.props;

    let cls = classNames(Classes.APP_LAYOUT_BLANK, className, {
      [Classes.APP_LAYOUT_BLANK_HCENTER]: horizontalCenter,
      [Classes.APP_LAYOUT_BLANK_VCENTER]: verticalCenter,
    });
    const contentWrapperCls = classNames(
      Classes.APP_LAYOUT_BLANK_CONTENT,
      contentWrapperClassName
    );
    return (
      <div className={cls}>
        <div
          className={contentWrapperCls}
          style={{ maxWidth: noDefaultContentWidth ? undefined : contentWidth }}
        >
          <AppLayoutWaitForQueryData queries={queries ?? []}>
            {() => (typeof children === 'function' ? children() : children)}
          </AppLayoutWaitForQueryData>
        </div>
      </div>
    );
  }
}

// ----------------------------------------------------------

export interface IAppLayoutBaseProps extends ILayoutProps {}

export type LeftPanelProps = {
  className?: string;
  panelWidth?: number;
  content?: React.ReactNode;
};
export enum SidePanelItemsPlacementEnum {
  SIDE = 'SIDE',
  IN_NAVBAR = 'IN_NAVBAR',
}
export enum SidePanelItemActionEnum {
  POPUP = 'POPUP',
  PIN_PANEL = 'PIN_PANEL',
  ON_CLICK = 'ON_CLICK',
}
export type SidePanelProps = {
  items: SidePanelItem[];
  panelClassName?: string;
  menuClassName?: string;
  panelWidth?: number;
  maxWidth?: number;
  /*
   *Default hodnota je SIDE
   */
  itemsPlacement?: SidePanelItemsPlacementEnum;
};
export type ContentFunction = (close: () => void) => React.ReactNode;
export type SidePanelItem = {
  icon: JSX.Element;
  key: string;
  label?: string;
  /**
   * V případě že je itemPlacement SIDE/undefined tak předáváme content jako React.FC
   * V případě že je itemPlacement IN_NAVBAR a potřebujeme použít funcli close pro zavření vybraného contentu tak předáváme content jako ContentFunction
   * */
  content: React.ReactNode | ContentFunction;
  /*
   * Používá se pouze v případě, že itemsPlacement je IN_NAVBAR
   */
  action?: SidePanelItemActionEnum;
  /*
   * Používá se pouze v případě, že itemsPlacement je IN_NAVBAR a action je ON_CLICK
   */
  onClick?: () => void;
  /**
   * Přidá kolem navigačního tlačítka component BadgeCount.
   */
  count?: number;
  /**
   * BadgeCount intent, je-li předán count.
   */
  countIntent?: BadgeCountIntent;
};
export type LeftPanelLayoutProps = {
  leftPanel?: LeftPanelProps;
};

export type AppLayoutBaseProps = IAppLayoutBaseProps &
  NavBarMenuLayoutProps &
  SidePanelLayoutProps &
  LeftPanelLayoutProps;

/**
 * Layout komponenta aplikace - s navbarem - využití např. seznamy apod.
 */
export class AppLayoutBase extends React.PureComponent<AppLayoutBaseProps> {
  render() {
    const {
      className,
      children,
      contentWrapperClassName,
      navbar,
      horizontalCenter,
      verticalCenter,
      sidePanel,
      leftPanel,
      contentWidth = defaultContentWidth,
      noDefaultContentWidth,
      queries,
    } = this.props;

    let cls = classNames(Classes.APP_LAYOUT_BASE, className, {
      [Classes.APP_LAYOUT_BASE_HCENTER]: horizontalCenter,
      [Classes.APP_LAYOUT_BASE_VCENTER]: verticalCenter,
    });
    const contentWrapperCls = classNames(
      Classes.APP_LAYOUT_BASE_CONTENT_WRAPPER,
      contentWrapperClassName
    );
    const sidePanelLocation =
      sidePanel?.itemsPlacement || SidePanelItemsPlacementEnum.IN_NAVBAR;
    return (
      <AppLayoutTestWrapper>
        <div className={cls}>
          <NavBarMenuLayout
            sidePanel={
              sidePanelLocation === SidePanelItemsPlacementEnum.IN_NAVBAR
                ? sidePanel
                : undefined
            }
            navbar={navbar}
          >
            <AppLayoutWaitForQueryData queries={queries ?? []}>
              {() => (
                <div className={Classes.APP_LAYOUT_BASE_NAVBAR_CONTENT_WRAPPER}>
                  {leftPanel && (
                    <div
                      style={{
                        width: leftPanel.panelWidth || defaultLeftPanelWidth,
                      }}
                      className={classNames(
                        Classes.APP_LAYOUT_BASE_LEFT_PANEL,
                        leftPanel.className
                      )}
                    >
                      {leftPanel.content}
                    </div>
                  )}
                  <div
                    className={contentWrapperCls}
                    style={{
                      maxWidth: noDefaultContentWidth
                        ? undefined
                        : contentWidth,
                    }}
                  >
                    {typeof children === 'function' ? children() : children}
                    {/*<div className={Classes.APP_LAYOUT_BASE_CONTENT}>{children}</div>*/}
                  </div>
                </div>
              )}
            </AppLayoutWaitForQueryData>
          </NavBarMenuLayout>
          {sidePanel &&
            sidePanelLocation === SidePanelItemsPlacementEnum.SIDE && (
              <SidePanel {...sidePanel} />
            )}
        </div>
      </AppLayoutTestWrapper>
    );
  }
}

// ----------------------------------------------------------

export interface IAppLayoutViewDetailProps extends ILayoutProps {
  header: React.ReactNode;
}

export type SidePanelLayoutProps = {
  sidePanel?: SidePanelProps;
};
/*
 * Umístění headeru vzhledem k obsahu, top je default
 */
export enum HeaderPlacementEnum {
  TOP = 'top',
  LEFT = 'left',
  /*BOTTOM = 'bottom',
  RIGHT = 'right',*/
}
export type AppLayoutViewDetailProps = IAppLayoutViewDetailProps &
  NavBarMenuLayoutProps &
  SidePanelLayoutProps & {
    headerPlacement?: HeaderPlacementEnum;
    headerIntent?: AppLayoutDetailHeaderIntent;
  };

/**
 * Layout komponenta aplikace - s navbarem a detail hlavičkou - využití např. detaily apod.
 */
export class AppLayoutViewDetail extends React.PureComponent<AppLayoutViewDetailProps> {
  render() {
    const {
      className,
      contentWrapperClassName,
      horizontalCenter,
      verticalCenter,
      navbar,
      header,
      children,
      sidePanel,
      contentWidth = defaultContentWidth,
      headerPlacement = HeaderPlacementEnum.TOP,
      headerIntent,
      noDefaultContentWidth,
      queries,
    } = this.props;

    let cls = classNames(Classes.APP_LAYOUT_VIEW_DETAIL, className, {
      [Classes.APP_LAYOUT_VIEW_DETAIL_HCENTER]: horizontalCenter,
      [Classes.APP_LAYOUT_VIEW_DETAIL_VCENTER]: verticalCenter,
    });
    let detailHeaderCls = classNames(Classes.APP_LAYOUT_VIEW_DETAIL_HEADER, {
      [Classes.APP_LAYOUT_VIEW_DETAIL_HEADER_DEFAULT]:
        !headerIntent || headerIntent === 'default',
      [Classes.APP_LAYOUT_VIEW_DETAIL_HEADER_SECONDARY]:
        headerIntent === 'secondary',
    });
    const contentWrapperCls = classNames(
      Classes.APP_LAYOUT_VIEW_DETAIL_CONTENT,
      contentWrapperClassName
    );
    const sidePanelLocation =
      sidePanel?.itemsPlacement || SidePanelItemsPlacementEnum.IN_NAVBAR;
    return (
      <AppLayoutTestWrapper>
        <div className={cls}>
          <NavBarMenuLayout
            sidePanel={
              sidePanelLocation === SidePanelItemsPlacementEnum.IN_NAVBAR
                ? sidePanel
                : undefined
            }
            navbar={navbar}
          >
            <AppLayoutWaitForQueryData queries={queries ?? []}>
              {() => (
                <React.Fragment>
                  {headerPlacement === HeaderPlacementEnum.TOP && (
                    <>
                      <div className={detailHeaderCls}>{header}</div>
                      <div
                        className={classNames(
                          Classes.APP_LAYOUT_VIEW_DETAIL_CONTENT_WRAPPER
                        )}
                      >
                        <div
                          className={contentWrapperCls}
                          style={{
                            maxWidth: noDefaultContentWidth
                              ? undefined
                              : contentWidth,
                          }}
                        >
                          {typeof children === 'function'
                            ? children()
                            : children}
                        </div>
                      </div>
                    </>
                  )}
                  {headerPlacement === HeaderPlacementEnum.LEFT && (
                    <div
                      className={
                        Classes.APP_LAYOUT_VIEW_DETAIL_CONTENT_WRAPPER_SIDE
                      }
                    >
                      <div
                        className={classNames(
                          Classes.APP_LAYOUT_VIEW_DETAIL_HEADER_LEFT,
                          {
                            [Classes.APP_LAYOUT_VIEW_DETAIL_HEADER_SECONDARY]:
                              headerIntent === 'secondary',
                          }
                        )}
                      >
                        {header}
                      </div>
                      <div
                        className={classNames(
                          Classes.APP_LAYOUT_VIEW_DETAIL_CONTENT_WRAPPER
                        )}
                      >
                        <div
                          className={contentWrapperCls}
                          style={{
                            maxWidth: noDefaultContentWidth
                              ? undefined
                              : contentWidth,
                          }}
                        >
                          {typeof children === 'function'
                            ? children()
                            : children}
                        </div>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              )}
            </AppLayoutWaitForQueryData>
          </NavBarMenuLayout>
          {sidePanel &&
            sidePanelLocation === SidePanelItemsPlacementEnum.SIDE && (
              <SidePanel {...sidePanel} />
            )}
        </div>
      </AppLayoutTestWrapper>
    );
  }
}
